/**
 * Filling in sentiment/intent badges that were not ready when the list loaded.
 *
 * The mailbox poll stores new messages and COMMITS them, then classifies each
 * one with an LLM call in a loop (mailbox_monitor.poll_single_mailbox). Mail
 * therefore becomes visible several seconds — sometimes minutes, on a busy
 * poll — before its sentiment is written, and nothing told the open inbox to
 * look again. A reply showed up with no sentiment or intent, and only a manual
 * page refresh filled it in (AB#22328).
 *
 * Committing the messages first is the right call — mail must not be held
 * hostage to a slow or failing LLM — so the fix belongs here: while the list
 * on screen still has messages awaiting classification, refetch quietly until
 * they land.
 *
 * Bounded on purpose. Some messages never get a sentiment (classification
 * failed, no LLM key, a message type the classifier skips), so an unbounded
 * poll would hammer the API forever on a list that will never settle. After
 * MAX_ATTEMPTS the page stops and leaves the manual "analyze unanalyzed"
 * backfill as the way out.
 */

export const SENTIMENT_POLL_INTERVAL_MS = 4000
export const SENTIMENT_POLL_MAX_ATTEMPTS = 15

interface MessageLike {
  id: number
  sentiment?: string | null
}

/** Messages on screen that have no sentiment yet. */
export function countAwaitingSentiment(messages: readonly MessageLike[] | null | undefined): number {
  if (!Array.isArray(messages)) return 0
  return messages.filter(m => !m?.sentiment).length
}

/**
 * Whether to schedule another quiet refetch.
 *
 * `documentHidden` keeps a backgrounded tab from polling — the badges will be
 * refreshed by the effect re-running when the list next changes, and a hidden
 * tab has nobody to show them to.
 */
export function shouldPollForSentiment(
  messages: readonly MessageLike[] | null | undefined,
  attempts: number,
  documentHidden = false,
): boolean {
  if (documentHidden) return false
  if (attempts >= SENTIMENT_POLL_MAX_ATTEMPTS) return false
  return countAwaitingSentiment(messages) > 0
}

/**
 * A key identifying "the set of messages currently awaiting classification".
 *
 * The attempt counter resets when this changes, so a newly-arrived batch gets
 * its own budget instead of inheriting an exhausted one from the previous
 * list. Ids are sorted so ordering changes alone do not reset the budget.
 */
export function awaitingSentimentKey(
  messages: readonly MessageLike[] | null | undefined,
): string {
  if (!Array.isArray(messages)) return ''
  return messages
    .filter(m => !m?.sentiment)
    .map(m => m.id)
    .sort((a, b) => a - b)
    .join(',')
}
